/**
 * User Menu Component
 *
 * Dropdown menu displaying user email and logout button.
 * Located in header/navbar for easy access.
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import { AccountCircle, Logout } from '@mui/icons-material';
import { useAuthUser, useAuthActions } from '../hooks/useAuthHooks';
import { useNavigate } from 'react-router-dom';

/**
 * UserMenu - User account dropdown menu
 *
 * Features:
 * - Display user email
 * - Logout button with redirect to login
 */
const UserMenu: React.FC = () => {
  const user = useAuthUser();
  const { logout } = useAuthActions();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  if (!user) {
    return null;
  }

  return (
    <Box>
      {/* User Menu Button */}
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        sx={{ ml: 2 }}
        aria-label="account of current user"
        aria-controls="account-menu"
        aria-haspopup="true"
      >
        <AccountCircle />
      </IconButton>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Email Display */}
        <MenuItem disabled>
          <Box sx={{ px: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Logged in as
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user.email}
            </Typography>
          </Box>
        </MenuItem>

        <Divider />

        {/* Logout Button */}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
